import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Progress } from 'antd';
import styled from 'styled-components';

import { useSmartImportStore } from '@/features/smartImport/stores/smartImport.store';
import { FileTypeIcon } from '@/shared/components/FileTypeIcon';
import { COLORS } from '@/shared/constants/colors';
import { useMockProgress } from '@/shared/hooks/useMockProgress';
import { FileMetaInfoDto } from '@/shared/lib/sj-orm/models/file-meta-info.dto';

type FileProps = {
  file: FileMetaInfoDto;
};

export const File: FC<FileProps> = ({ file }) => {
  const { t } = useTranslation('features/smartImport', { keyPrefix: 'fileProcessing' });
  const siStoreData = useSmartImportStore((s) => s.data);
  const { mockProgress, setMockProgress, intervalId } = useMockProgress();

  const isCompleted = siStoreData?.results.some((result) => result.file_id === file.id);
  const isFailed = siStoreData?.errors.some((error) => error.file_id === file.id);

  useEffect(() => {
    if ((isCompleted || isFailed) && intervalId.current) {
      clearInterval(intervalId.current);
      setMockProgress(100);
    }
  }, [isCompleted, isFailed]);

  return (
    <Container>
      <FileTypeIcon
        ext={file.extension.replace('.', '')}
        size="s36"
        style={{ color: COLORS.systemDark200 }}
      />
      <DetailsWrapper>
        <TitleWrapper>
          <FileName>{file.name}</FileName>
        </TitleWrapper>
        <StatusWrapper>
          <ProgressBar
            percent={mockProgress}
            showInfo={false}
            strokeColor={
              isCompleted ? COLORS.systemForest200 : isFailed ? COLORS.error : COLORS.systemSky200
            }
            size="small"
          />
          <StatusText>{isCompleted ? t('done') : isFailed ? t('error') : t('pending')}</StatusText>
        </StatusWrapper>
      </DetailsWrapper>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
`;

const DetailsWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: 700;
  line-height: 20px;
`;

const FileName = styled.p`
  flex: 1;
`;

const StatusWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const StatusText = styled.p`
  font-size: 12px;
  line-height: 16px;
  color: ${COLORS.systemDark200};
`;

const ProgressBar = styled(Progress)`
  &&&.ant-progress {
    line-height: 1;
    font-size: 6px;
  }
`;
